import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Callout,
  EmptyState,
  Heading,
  Spinner,
  Table,
  Tag,
  type TableColumn,
} from '@humanforce/mango-ui-react';
import type {
  PayRunPopulationIssuePage,
  PayRunTransactionIssuePage,
  PopulationLoadStatus,
} from './payRunPopulationApiTypes';
import { getPopulationIssues, getTransactionIssues } from './payRunPopulationHttpClient';

interface PayRunExceptionsTabProps {
  payId: string;
  populationStatus: PopulationLoadStatus | null | undefined;
}

const pageSize = 20;

export const formatPopulationIssueCode = (code: string): string => code
  .replace(/^population_/, '')
  .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
  .split('_')
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

export const PayRunExceptionsTab = ({ payId, populationStatus }: PayRunExceptionsTabProps) => {
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<PayRunPopulationIssuePage | null>(null);
  const [transactionResult, setTransactionResult] = useState<PayRunTransactionIssuePage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const [nextPopulation, nextTransactions] = await Promise.all([
        getPopulationIssues(payId, { page, pageSize }, signal),
        getTransactionIssues(payId, { page: 1, pageSize: 100 }, signal),
      ]);
      setResult(nextPopulation);
      setTransactionResult(nextTransactions);
    } catch (loadError) {
      if (!signal?.aborted) {
        setError(loadError instanceof Error ? loadError.message : 'Exceptions could not be loaded.');
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [page, payId]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load, populationStatus]);

  const columns = useMemo<TableColumn[]>(() => [
    {
      field: 'issueCode',
      header: 'Exception',
      body: (row) => (
        <span className="mgo-stack mgo-gap-2xs">
          <strong>{formatPopulationIssueCode(row.issueCode)}</strong>
          <span className="mgo-text-xs mgo-color-muted">{row.detail}</span>
        </span>
      ),
    },
    {
      field: 'fieldName',
      header: 'Field',
      body: (row) => row.fieldName || '—',
    },
    {
      field: 'severity',
      header: 'Severity',
      body: (row) => (
        <Tag
          label={row.severity === 'blocking' ? 'Cannot start pay' : row.severity === 'review_required' ? 'Review required' : 'Warning'}
          tone={row.severity === 'blocking' ? 'critical' : 'caution'}
        />
      ),
    },
    {
      field: 'affectedPersonCount',
      header: 'Affected',
      body: (row) => `${row.affectedPersonCount} ${row.affectedPersonCount === 1 ? 'person' : 'people'}`,
    },
  ], []);

  const transactionColumns = useMemo<TableColumn[]>(() => [
    {
      field: 'code',
      header: 'Exception',
      body: (row) => (
        <span className="mgo-stack mgo-gap-2xs">
          <strong>{row.code === 'transaction_out_of_period'
            ? 'Date worked outside pay period'
            : formatPopulationIssueCode(row.code)}</strong>
          <span className="mgo-text-xs mgo-color-muted">{row.reason}</span>
        </span>
      ),
    },
    { field: 'sourceRecordId', header: 'Source record', body: (row) => row.sourceRecordId || '—' },
    { field: 'field', header: 'Field', body: (row) => row.field || '—' },
    {
      field: 'severity',
      header: 'Severity',
      body: () => <Tag label="Review required" tone="caution" />,
    },
  ], []);

  return (
    <div className="mgo-stack mgo-gap-xl" aria-busy={loading}>
      {!loading && !error && transactionResult && transactionResult.items.length > 0 && (
        <section aria-label="Transaction input exceptions" className="mgo-stack mgo-gap-m">
          <div className="mgo-stack mgo-gap-2xs">
            <Heading level={2}>Transaction input exceptions</Heading>
            <p className="mgo-text-s mgo-color-muted">
              These imported records were retained for review but were not included in this Pay Run calculation.
            </p>
          </div>
          <div className="mgo-cluster mgo-gap-s">
            {transactionResult.blocking > 0 && (
              <Tag label={`${transactionResult.blocking} blocking`} tone="critical" />
            )}
            <Tag label={`${transactionResult.reviewRequired} review required`} tone="caution" />
          </div>
          <div style={{ overflowX: 'auto' }}>
            <Table
              value={transactionResult.items}
              columns={transactionColumns}
              scrollable
              emptyMessage="No transaction input exceptions."
            />
          </div>
        </section>
      )}

      <section aria-label="Population exceptions" className="mgo-stack mgo-gap-l">
        <Heading level={2}>Population exceptions</Heading>
      {result && result.summary.blocking > 0 && (
        <Callout tone="critical" title="Cannot start pay">
          {result.summary.blocking} critical {result.summary.blocking === 1 ? 'exception must' : 'exceptions must'} be cleared before this Pay Run can progress.
        </Callout>
      )}

      {result && (
        <div className="mgo-cluster mgo-gap-s" aria-label="Exception summary">
          <Tag label={`${result.summary.blocking} blocking`} tone="critical" />
          <Tag label={`${result.summary.warnings} warnings`} tone="caution" />
          {result.summary.reviewRequired > 0 && (
            <Tag label={`${result.summary.reviewRequired} review required`} tone="caution" />
          )}
          <span className="mgo-text-xs mgo-color-muted">{result.summary.affectedPeople} affected people</span>
        </div>
      )}

      {populationStatus === 'loading' && (
        <Callout tone="info" title="Refreshing exceptions">
          Exception results will update when the new employee snapshot is ready.
        </Callout>
      )}

      {error && (
        <Callout
          tone="critical"
          title="Exceptions unavailable"
          action={<Button label="Retry" variant="secondary" size="small" onClick={() => void load()} />}
        >
          {error}
        </Callout>
      )}

      {loading && !error ? (
        <div className="mgo-cluster mgo-justify-center mgo-gap-s" style={{ minHeight: '12rem' }}>
          <Spinner /> <span>Loading exceptions…</span>
        </div>
      ) : !error && result && result.revisionNumber == null ? (
        <EmptyState
          variant="no-data"
          title="No employee population loaded"
          description="Load employees from the Employees tab to see population exceptions."
        />
      ) : !error && result && result.items.length === 0 ? (
        <EmptyState
          variant="success"
          title="No population exceptions"
          description="The active employee snapshot has no population issues."
        />
      ) : !error && result ? (
        <div style={{ overflowX: 'auto' }}>
          <Table value={result.items} columns={columns} scrollable emptyMessage="No exceptions." />
        </div>
      ) : null}

      {!loading && !error && result && result.pagination.totalPages > 1 && (
        <nav aria-label="Exception pages" className="mgo-cluster mgo-justify-end mgo-gap-s">
          <span className="mgo-text-xs mgo-color-muted">
            Page {result.pagination.page} of {result.pagination.totalPages}
          </span>
          <Button label="Previous" variant="secondary" size="small" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))} />
          <Button label="Next" variant="secondary" size="small" disabled={page >= result.pagination.totalPages} onClick={() => setPage((current) => current + 1)} />
        </nav>
      )}
      </section>
    </div>
  );
};
